import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WOKRPAPER_SHEETS } from 'src/app/constants/workbook.constants';
import { SheetTableInfo, PermissionType, columnMap } from 'src/app/model';
import { ExcelHelperService } from '../addin-helper-services/excel-helper.service';

@Injectable({
  providedIn: 'root',
})
export class ReturnApprovalService {
  private loadingActionSubject = new BehaviorSubject<string | null>(null);

  constructor(private excelHelper: ExcelHelperService) {}

  //#region Loading Action
  
  get loadingAction$() {
    return this.loadingActionSubject.asObservable();
  }

  setLoadingAction(action: string | null): void {
    this.loadingActionSubject.next(action);
  }

  getLoadingAction(): string | null {
    return this.loadingActionSubject.value;
  }

  resetLoadingAction(): void {
    this.loadingActionSubject.next(null);
  }

  //#endregion

  async fetchSelectedRows(): Promise<any[]> {
    return this.excelHelper.getSelectedRowsObjectsFromActiveSheet();
  }

  async fetchSelectedRowsByColumnNames(columnNames: string[], tableName?: string): Promise<any[]> {
    return this.excelHelper.getSelectedRowsObjectsByColumnNamesFromActiveSheet(columnNames, tableName);
  }

  async approveRows(
    permission: PermissionType,
    selectedRows: any[],
    currentUserFullName: string,
    clientApprovalConstant: string,
    sheetTableInfo: SheetTableInfo,
    appContext: any
  ): Promise<void> {
    const targetCols = columnMap[permission];
    if (!targetCols) {
      throw new Error('Unknown permission type');
    }

    const approvalCellValue =
      permission === PermissionType.ApproveClientApproval
        ? clientApprovalConstant
        : currentUserFullName;

    await this.excelHelper.performApprovalWrite(
      WOKRPAPER_SHEETS.ReturnsCalendarSheet,
      appContext.workpaperPassword || '',
      selectedRows.map((row) => row.rowIndex),
      {
        [targetCols.userCol]: approvalCellValue,
        [targetCols.dateCol]: this.getCurrentDateTime(),
      }
    );
  }

  async undoApprovalRows(
    permission: PermissionType,
    selectedRows: any[],
    sheetTableInfo: SheetTableInfo,
    appContext: any
  ): Promise<void> {
    const targetCols = columnMap[permission];
    if (!targetCols) {
      throw new Error('Unknown permission type');
    }

    const password = appContext.workpaperPassword;
    if (typeof password !== 'string' || password.length === 0) {
      throw new Error('Missing workpaper password');
    }

    await this.excelHelper.performApprovalWrite(
      WOKRPAPER_SHEETS.ReturnsCalendarSheet,
      password,
      selectedRows.map((row) => row.rowIndex),
      {
        [targetCols.userCol]: '',
        [targetCols.dateCol]: '',
      }
    );
  }

  private getCurrentDateTime(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timeString = `${hours}:${minutes} ${ampm}`;
    return `${month}/${day}/${year}  ${timeString}`;
  }
}
